<template>
  <div v-if="event" class="store-event" :style="{ 'background-color': mainSectionBackgroundColor }">
    <!-- Header with logos and banner -->
    <div class="store-event__header">
      <div v-if="headerLeftImage || headerRightImage" class="store-event__header-logos" :style="{ 'background-color': headerBackgroundColor }">
        <div class="store-event__header-logo-left">
          <img v-if="headerLeftImage" :src="headerLeftImage" alt="Logo" />
        </div>
        <div class="store-event__header-logo-right">
          <img v-if="headerRightImage" :src="headerRightImage" alt="Logo" />
        </div>
      </div>
      <div class="store-event__banner">
        <v-img v-if="bannerImage" :src="bannerImage" cover />
      </div>
    </div>

    <!-- Title Section -->
    <div class="store-event__title-section">
      <h1 class="store-event__title" :style="{ color: mainSectionFontColor }">{{ event.hotsiteConfiguration?.content?.title || event.title }}</h1>
      <p class="store-event__description" :style="{ color: mainSectionFontColor }">
        {{ event.hotsiteConfiguration?.content?.description || event.description }}
      </p>
    </div>

    <!-- Event Details Section -->
    <div class="store-event__details-section">
      <div class="store-event__details-divider store-event__details-divider--top" :style="{ 'background-color': dividerColor }"></div>
      <div class="store-event__details-item mb-6">
        <div class="store-event__details-label" :style="{ color: mainSectionFontColor }">Data e hora do evento</div>
        <div class="store-event__details-value" :style="{ color: mainSectionFontColor }">{{ formattedEventDate }}</div>
      </div>
      <div class="store-event__details-item">
        <div class="store-event__details-label" :style="{ color: mainSectionFontColor }">Local</div>
        <div class="store-event__details-value" :style="{ color: mainSectionFontColor }">{{ eventLocation }}</div>
      </div>
      <div class="store-event__details-divider store-event__details-divider--bottom" :style="{ 'background-color': dividerColor }"></div>
    </div>

    <!-- Dynamic Content Cards -->
    <dynamic-content-card
      v-for="(item, index) in dynamicContent"
      :key="index"
      :title="item.title"
      :description="item.description"
      :background-color="item.backgroundColor"
      :font-color="item.fontColor"
    />

    <!-- Confirmation Form -->
    <confirmation-card
      :initial-guest="guest"
      :initial-companions="companions"
      :confirmed="confirmed"
      :max-companions="maxCompanions"
      :consumer-terms="consumerTerms"
      :confirmation-title="event.hotsiteConfiguration?.content?.confirmationTitle || 'Confirmação de presença'"
      :confirmation-description="
        event.hotsiteConfiguration?.content?.confirmationDescription ||
        'Para participar deste evento exclusivo precisamos que você confirme o seu dados e envie suas informações para o nosso time. Você também pode convidar 1 acompanhante'
      "
      :confirmation-section-background-color="confirmationSectionBackgroundColor"
      :confirmation-section-font-color="confirmationSectionFontColor"
      :confirmation-section-button-background-color="confirmationSectionButtonBackgroundColor"
      :confirmation-section-button-font-color="confirmationSectionButtonFontColor"
      :confirmation-start-date="event?.confirmationStartDate"
      :confirmation-end-date="event?.confirmationEndDate"
      :is-confirmation-limit-reached="event?.isConfirmationLimitReached"
      @confirmed="onConfirmed"
    />

    <!-- Custom confirmed popup (replaces Swal when configured) -->
    <confirmed-popup
      v-if="confirmedPopup"
      v-model="confirmedPopupDialogVisible"
      :title="confirmedPopup.title"
      :description="confirmedPopup.description"
      :background-color="confirmedPopup.backgroundColor"
      :font-color="confirmedPopup.fontColor"
      :button-background-color="confirmedPopup.buttonBackgroundColor"
      :button-font-color="confirmedPopup.buttonFontColor"
    />

    <no-slots-popup
      v-model="noSlotsDialogVisible"
      :background-color="confirmationSectionBackgroundColor"
      :font-color="confirmationSectionFontColor"
      :interest-button-background-color="confirmationSectionButtonBackgroundColor"
      :interest-button-font-color="confirmationSectionButtonFontColor"
      :interest-loading="noSlotsInterestLoading"
      @interest="onNoSlotsInterest"
    />

    <!-- Location Section -->
    <div class="store-event__location-section">
      <h2 class="store-event__location-title" :style="{ color: mainSectionFontColor }">Localização</h2>
      <div v-if="googleMapsEmbedUrl" class="store-event__map-container">
        <iframe :src="googleMapsEmbedUrl" style="border: 0" allowfullscreen="" loading="lazy" referrerpolicy="no-referrer-when-downgrade"></iframe>
      </div>

      <div v-else class="store-event__location-address" :style="{ color: mainSectionFontColor }">
        <p>{{ event.fullAddress }}</p>
      </div>
    </div>
  </div>
</template>

<script>
import Swal from 'sweetalert2'
import { mapActions, mapGetters } from 'vuex'
import ConfirmationCard from '../components/ConfirmationCard.vue'
import ConfirmedPopup from '../components/ConfirmedPopup.vue'
import DynamicContentCard from '../components/DynamicContentCard.vue'
import NoSlotsPopup from '../components/NoSlotsPopup.vue'

export default {
  name: 'StoreEvent',
  components: {
    ConfirmationCard,
    ConfirmedPopup,
    DynamicContentCard,
    NoSlotsPopup
  },
  data() {
    return {
      confirmedPopupDialogVisible: false,
      hasAutoOpenedConfirmedPopup: false,
      noSlotsDialogVisible: false,
      noSlotsInterestLoading: false
    }
  },
  computed: {
    ...mapGetters(['GET_STORE_EVENT', 'GET_STORE_EVENT_GUEST', 'GET_STORE_EVENT_CONFIRMED', 'GET_STORE_EVENT_COMPANIONS']),
    event() {
      return this.GET_STORE_EVENT
    },
    guest() {
      return this.GET_STORE_EVENT_GUEST
    },
    confirmed() {
      return this.GET_STORE_EVENT_CONFIRMED
    },
    companions() {
      return this.GET_STORE_EVENT_COMPANIONS
    },
    maxCompanions() {
      return this.event?.numberOfCompanions || 0
    },
    consumerTerms() {
      return this.event?.consumerTerms || ''
    },
    headerBackgroundColor() {
      return this.event?.hotsiteConfiguration?.assets?.colors?.headerBackground || '#ffffff'
    },
    mainSectionBackgroundColor() {
      return this.event?.hotsiteConfiguration?.assets?.colors?.mainSectionBackground || '#ffffff'
    },
    mainSectionFontColor() {
      return this.event?.hotsiteConfiguration?.assets?.colors?.mainSectionFont || '#000000'
    },
    dividerColor() {
      const color = this.mainSectionFontColor
      // Convert hex to rgba with 0.2 opacity
      if (color.startsWith('#')) {
        const hex = color.slice(1)
        const r = parseInt(hex.substring(0, 2), 16)
        const g = parseInt(hex.substring(2, 4), 16)
        const b = parseInt(hex.substring(4, 6), 16)
        return `rgba(${r}, ${g}, ${b}, 0.2)`
      }
      // If already rgba/rgb, try to extract and modify
      if (color.startsWith('rgb')) {
        return color.replace(/\)$/, ', 0.2)').replace('rgb', 'rgba')
      }
      // Fallback
      return 'rgba(0, 0, 0, 0.2)'
    },
    confirmationSectionBackgroundColor() {
      return this.event?.hotsiteConfiguration?.assets?.colors?.confirmationSectionBackground || '#f5f5f5'
    },
    confirmationSectionFontColor() {
      return this.event?.hotsiteConfiguration?.assets?.colors?.confirmationSectionFont || '#000000'
    },
    confirmationSectionButtonBackgroundColor() {
      return this.event?.hotsiteConfiguration?.assets?.colors?.confirmationSectionButtonBackground || '#e0e0e0'
    },
    confirmationSectionButtonFontColor() {
      return this.event?.hotsiteConfiguration?.assets?.colors?.confirmationSectionButtonFont || '#000000'
    },
    headerLeftImage() {
      return this.event?.hotsiteConfiguration?.assets?.images?.header?.left
    },
    headerRightImage() {
      return this.event?.hotsiteConfiguration?.assets?.images?.header?.right
    },
    bannerImage() {
      const windowWidth = typeof window !== 'undefined' ? window.innerWidth : 1920
      const images = this.event?.hotsiteConfiguration?.assets?.images?.banner
      if (windowWidth < 960 && images?.smartphone) {
        return images.smartphone
      }
      return images?.desktop
    },
    formattedEventDate() {
      if (!this.event?.eventDate) return ''
      const date = this.$moment(this.event.eventDate)
      const formattedDate = date.format('DD/MM/YYYY')
      const time = date.format('HH:mm')
      return `${formattedDate} às ${time}`
    },
    eventLocation() {
      return this.event.fullAddress
    },
    dynamicContent() {
      return this.event?.hotsiteConfiguration?.dynamicContent || []
    },
    confirmedPopup() {
      return this.event?.hotsiteConfiguration?.confirmedPopup || null
    },
    googleMapsEmbedUrl() {
      const placeLink = this.event?.googleMapsPlaceLink

      if (!placeLink) return null

      // If it's already an embed URL, use it directly
      if (placeLink.includes('/maps/embed')) {
        return placeLink
      }

      // Try to use coordinates if available
      if (this.event?.address?.coordinates) {
        const lat = this.event.address.coordinates.lat
        const lng = this.event.address.coordinates.lng
        return `https://www.google.com/maps?q=${lat},${lng}&hl=pt&z=15&output=embed`
      }
      // If no coordinates, try address
      if (this.event?.fullAddress) {
        return `https://www.google.com/maps?q=${encodeURIComponent(this.event.fullAddress)}&hl=pt&output=embed`
      }

      return null
    }
  },
  watch: {
    confirmed: {
      handler: 'syncEnterPopups',
      immediate: true
    },
    confirmedPopup: 'maybeOpenConfirmedPopupOnEnter'
  },
  mounted() {
    // Second pass after mount: `confirmed` immediate covers the common path; this is idempotent and
    // helps if store/event timing ever falls between created and mounted (e.g. navigation edge cases).
    this.syncEnterPopups()
  },
  methods: {
    ...mapActions(['registerStoreEventFutureInterest']),
    syncEnterPopups() {
      if (this.maybeOpenConfirmedPopupOnEnter()) {
        return
      }
      this.maybeOpenNoSlotsPopup()
    },
    maybeOpenNoSlotsPopup() {
      if (this.event?.isConfirmationLimitReached && !this.confirmed) {
        this.noSlotsDialogVisible = true
        return true
      }
      this.noSlotsDialogVisible = false
      return false
    },
    maybeOpenConfirmedPopupOnEnter() {
      if (this.confirmed && this.confirmedPopup && !this.hasAutoOpenedConfirmedPopup) {
        this.hasAutoOpenedConfirmedPopup = true
        this.confirmedPopupDialogVisible = true
        this.noSlotsDialogVisible = false
        return true
      }
      return false
    },
    async onNoSlotsInterest() {
      this.noSlotsInterestLoading = true
      try {
        await this.registerStoreEventFutureInterest()
        this.$snackbar({
          message: 'Obrigado! Registramos seu interesse em próximos eventos.',
          snackbarColor: 'success'
        })
        this.noSlotsDialogVisible = false
      } catch (e) {
        this.$snackbar({
          message: 'Não foi possível registrar seu interesse. Tente novamente mais tarde.',
          snackbarColor: 'error'
        })
      } finally {
        this.noSlotsInterestLoading = false
      }
    },
    onConfirmed() {
      if (this.confirmedPopup) {
        this.confirmedPopupDialogVisible = true
      } else {
        Swal.fire({
          icon: 'success',
          title: 'Presença confirmada!',
          text: 'Sua presença foi confirmada com sucesso.',
          showConfirmButton: false,
          timer: 3000,
          timerProgressBar: true
        })
      }
    }
  }
}
</script>

<style scoped lang="scss">
.store-event {
  min-height: 100vh;
}

.store-event__header {
  width: 100%;
}

.store-event__header-logos {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 4px 12px;
  width: 100%;
}

.store-event__header-logo-left,
.store-event__header-logo-right {
  img {
    max-height: 40px;
    max-width: 150px;
  }
}

.store-event__banner {
  width: 100%;
  aspect-ratio: 5 / 1;
  max-height: 300px;
  max-width: 1500px; // 300px * 5 (aspect ratio)
  margin: 0 auto; // Center when it reaches max-width
  overflow: hidden;

  // Fallback for older browsers
  @supports not (aspect-ratio: 5 / 1) {
    height: 400px;
  }

  // Mobile uses same aspect ratio with smaller max height
  @media (max-width: 960px) {
    max-height: 300px;
    max-width: 1500px; // Keep same max-width on mobile too
  }
}

.store-event__details-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 24px;
  max-width: 700px;
  margin: 40px auto;
  position: relative;
}

.store-event__details-divider {
  position: absolute;
  left: 24px;
  right: 24px;
  height: 1px;

  &--top {
    top: 0;
  }

  &--bottom {
    bottom: 0;
  }
}

.store-event__details-item {
  flex: 1;
  text-align: center;
}

.store-event__details-label {
  font-size: 14px;
  margin-bottom: 4px;
  opacity: 0.7;
}

.store-event__details-value {
  font-size: 18px;
  font-weight: 500;
}

.store-event__title-section {
  text-align: center;
  padding: 40px 24px 0;
  max-width: 700px;
  margin: 0 auto;
}

.store-event__title {
  font-size: 2.5rem;
  font-weight: bold;
  margin-bottom: 24px;
}

.store-event__description {
  font-size: 1rem;
  line-height: 1.6;
  max-width: 800px;
  margin: 0 auto;
  opacity: 0.8;
}

.store-event__location-section {
  text-align: center;
  padding: 40px 24px;
  max-width: 1200px;
  margin: 0 auto;
}

.store-event__location-title {
  font-size: 2rem;
  font-weight: bold;
  margin-bottom: 16px;
}

.store-event__location-description {
  font-size: 1rem;
  margin-bottom: 24px;
  opacity: 0.8;
}

.store-event__location-address {
  font-size: 1rem;
  margin-bottom: 24px;
}

.store-event__map-container {
  width: 100%;
  max-width: 900px;
  margin: 24px auto 0;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

  iframe {
    display: block;
    width: 100%;
    height: 300px;
    border: none;
  }
}
</style>
